import { TCompanies } from '../../shared/const'

/*
 * Der Weg einer Junior Company durch das Programm — als Liste von Schritten,
 * gruppiert in vier Abschnitte.
 *
 * Die Reihenfolge stand bis 09.08.2026 im Fortschrittsbalken selbst, jeder
 * Schritt mit einer festen Prozentzahl als Position (`position={35}`). Damit
 * war der Balken an eine Breite gebunden: Fiel ein Schritt weg (eine Junior
 * Basic Company verkauft keine Anteile) oder kam einer dazu, mussten alle
 * Zahlen von Hand nachgerückt werden — und zwei Beschriftungen überlappten
 * sich, wo die Zahlen zu nah beieinander lagen.
 *
 * Hier steht nur noch, **was** der Reihe nach zu tun ist. Wie breit ein
 * Schritt wird, entscheidet das Stylesheet.
 *
 * Ob ein Schritt erfüllt ist, entscheidet immer der Server
 * (`activationsStatus` aus `services/companies/activation`) — dieselbe
 * Auskunft, die die Spalte „Was als Nächstes zu tun ist"
 * (views/common/CompanySteps) liest.
 */

/**
 * Die Frist zu einem Bericht, je nach Unternehmensform. Fehlt der Wert in den
 * Einstellungen, bleibt der Schritt ohne Frist stehen (statt „Invalid Date").
 */
const berichtsFrist = (companyType, reportsDeadline, art) => {
	if (!reportsDeadline) return null

	const wert =
		companyType === TCompanies.JC
			? art === 'annual'
				? reportsDeadline.JC_annual
				: reportsDeadline.JC_semiannual
			: reportsDeadline[companyType]

	return wert ? new Date(wert) : null
}

/**
 * Baut die Abschnitte des Fortschrittsbalkens.
 *
 * @param companyType       JC | JBC | JCC
 * @param activationsStatus Erfüllt-Stand vom Server
 * @param isActivated       Unternehmen genehmigt
 * @param isNeedReview      zur Genehmigung eingereicht
 * @param reportsDeadline   Fristen aus den Einstellungen
 * @param t                 Übersetzer (react-i18next)
 * @returns { phases, steps, lane, done, total, percent, currentId }
 */
export const buildCompanyProgress = ({
	companyType,
	activationsStatus = {},
	isActivated = false,
	isNeedReview = false,
	reportsDeadline = null,
	t
}) => {
	const status = activationsStatus || {}
	const istJC = companyType === TCompanies.JC

	const anmeldung = [
		{ id: 'hello', title: t('Hello'), icon: 'start', done: status.hello !== false },
		{ id: 'employees', title: t('Add Employees'), icon: 'team', done: !!status.employees || isActivated },
		{ id: 'experts', title: t('Add Experts'), icon: 'expert', done: !!status.experts || isActivated },
		{ id: 'riskAnalysis', title: t('Risk Analysis'), icon: 'analysis', done: !!status.riskAnalysis || isActivated },
		/*
		 * Der Businessplan ist keine Voraussetzung für die Genehmigung. Als
		 * gleichrangiger Punkt sah er aus wie eine Pflicht — deshalb zählt er
		 * nicht mit und ist als freiwillig gekennzeichnet.
		 */
		{
			id: 'businessPlan',
			title: t('Business Plan'),
			icon: 'document',
			optional: true,
			done: !!status.businessPlan
		}
	]

	/*
	 * Das Skills4Retail-Konzept steht nur im Balken, wo es auch wirklich
	 * sperrt. Die Einstellung kennt je Unternehmensform genau einen der beiden
	 * Fälle: vor der Aktivierung (`conceptApplies`) oder vor dem ersten
	 * Finanzbericht (`conceptAtFirstReport`). Ohne Sperre gibt es keine Spur;
	 * ein Schritt, den niemand gehen muss, verlängert den Balken nur.
	 *
	 * Bis Anforderung #209 (Oktober 2026) war es ein Knoten zwischen den
	 * anderen — in der Geschäftstätigkeit zwischen Anteilen und
	 * Halbjahresbericht. Das las sich, als könne das Konzept erst dort
	 * begonnen werden. Bearbeiten lässt es sich aber ab der Markt- und
	 * Risikoanalyse; nur die **Abgabe** liegt dort. Deshalb steht es jetzt als
	 * eigene Spur neben dem Balken (siehe `skills4RetailLane` unten), und der
	 * Hauptbalken hat keinen eigenen Knoten mehr dafür.
	 */
	const konzept = status.conceptApplies
		? {
				id: 'concept',
				title: t('Skills4Retail'),
				icon: 'document',
				done: !!status.concept || isActivated,
				// Muss vor dem Einreichen zur Genehmigung erledigt sein.
				dueBeforeId: 'review',
				phaseId: 'registration'
		  }
		: status.conceptAtFirstReport
		? {
				id: 'concept',
				title: t('Skills4Retail'),
				icon: 'document',
				done: status.conceptPassed === true,
				// Muss vor dem ersten Finanzbericht bewertet sein — intern bei
				// allen drei Unternehmensformen der Halbjahresbericht.
				dueBeforeId: 'reportSemiannual',
				phaseId: 'activity',
				deadline: berichtsFrist(companyType, reportsDeadline, 'semiannual')
		  }
		: null

	const genehmigung = [
		{ id: 'review', title: t('Check'), icon: 'waiting', done: isNeedReview || isActivated },
		{ id: 'approved', title: t('Approved'), icon: 'approved', done: isActivated }
	]

	const geschaeft = []

	// Eine Junior Basic Company verkauft keine Anteile. Bis 09.08.2026 stand
	// der Schritt trotzdem im Balken und wurde für JBC kurzerhand als erfüllt
	// gezählt — er behauptete damit etwas, das es dort gar nicht gibt.
	if (companyType !== TCompanies.JBC)
		geschaeft.push({ id: 'shares', title: t('Create Share'), icon: 'shares', done: !!status.shares })

	geschaeft.push({
		id: 'reportSemiannual',
		title: istJC ? t('Semi-annual Report') : t('Semester Report'),
		icon: 'report',
		done: !!status.reports?.Semiannual,
		deadline: berichtsFrist(companyType, reportsDeadline, 'semiannual')
	})

	if (istJC)
		geschaeft.push({
			id: 'reportAnnual',
			title: t('Annual Report'),
			icon: 'report',
			done: !!status.reports?.AnnualReport,
			deadline: berichtsFrist(companyType, reportsDeadline, 'annual')
		})

	const berichte = [
		{
			id: 'invoiceSemiannual',
			title: istJC ? t('Semi-annual Invoice') : t('Pay Invoice'),
			icon: 'invoice',
			done: !!status.invoices?.Semiannual
		}
	]

	if (istJC)
		berichte.push({
			id: 'invoiceAnnual',
			title: t('Annual Invoice'),
			icon: 'invoice',
			done: !!status.invoices?.AnnualReport
		})

	berichte.push({ id: 'finalReport', title: t('Business Report'), icon: 'document', done: !!status.finalReport })

	/*
	 * Die Urkunde. Sie ist der einzige Schritt, den niemand selbst geht: Sie
	 * entsteht, sobald der Geschäftsbericht hochgeladen und alle Rechnungen
	 * bezahlt sind. Bis 25.08.2026 endete der Balken beim Geschäftsbericht —
	 * das Ziel des ganzen Jahres kam darin nicht vor. Der Pokal stand deshalb
	 * am Geschäftsbericht; er gehört hierher.
	 */
	berichte.push({ id: 'certificate', title: t('Certificates'), icon: 'trophy', done: !!status.certificate })

	const phases = [
		{ id: 'registration', title: t('Registration'), steps: anmeldung },
		{ id: 'approval', title: t('Approval'), steps: genehmigung },
		{ id: 'activity', title: t('BUSINESS ACTIVITIES'), steps: geschaeft },
		{ id: 'reports', title: t('reports'), steps: berichte }
	]

	const steps = phases.flatMap(phase => phase.steps)

	const lane = skills4RetailLane(steps, konzept)

	/*
	 * Wo steht die Company gerade? Der erste Schritt des Hauptbalkens, der
	 * weder erledigt noch freiwillig ist. Sind alle erledigt, gibt es keinen
	 * laufenden mehr — dann läuft der Balken bis ans Ende.
	 *
	 * Das Konzept drängt sich erst vor, wenn seine Abgabe ansteht: wenn der
	 * nächste offene Schritt der ist, vor dem es abgegeben sein muss (oder ein
	 * späterer). Vorher ist es „jederzeit bearbeitbar" und hält nichts auf —
	 * „Jetzt an der Reihe" nennt dann weiter die Mitarbeiter:innen, die
	 * Genehmigung oder die Anteile. Steht die Abgabe an, ist es an der Reihe:
	 * Der Server lässt den Schritt dahinter ohne Konzept ohnehin nicht zu.
	 */
	const hauptCurrent = steps.find(step => !step.done && !step.optional)
	let current = hauptCurrent

	if (lane && !lane.step.done) {
		const hauptIndex = hauptCurrent ? steps.indexOf(hauptCurrent) : steps.length
		if (hauptIndex >= lane.endIndex) current = lane.step
	}

	/*
	 * Gezählt wird das Konzept weiter mit: Es ist eine Pflicht wie die
	 * anderen, nur ohne festen Platz in der Reihe. „x von 14 erledigt" bleibt
	 * damit dieselbe Zahl wie vor der Spur.
	 */
	const pflicht = [...steps, ...(lane ? [lane.step] : [])].filter(step => !step.optional)
	const done = pflicht.filter(step => step.done).length

	return {
		phases,
		steps,
		lane,
		currentId: current?.id || null,
		done,
		total: pflicht.length,
		percent: pflicht.length ? Math.round((done / pflicht.length) * 100) : 0
	}
}

/**
 * Die Spur des Skills4Retail-Konzepts neben dem Hauptbalken.
 *
 * Sie beginnt am Knoten „Markt- und Risikoanalyse" — ab dort lässt sich das
 * Konzept sinnvoll erarbeiten — und endet an der Grenze vor dem Schritt, den es
 * sperrt (Halbjahresbericht bzw. Prüfung). Dort ist die Abgabe.
 *
 * Positionen sind Spaltenindizes des Hauptbalkens: `startIndex` ist die Zelle,
 * auf deren Mitte die Spur beginnt, `endIndex` die Zelle, an deren linkem Rand
 * sie endet. Das Stylesheet rechnet daraus Prozent — so rückt die Spur mit,
 * wenn ein Schritt wegfällt (eine JBC verkauft keine Anteile).
 *
 * @returns null, wenn das Konzept hier nichts sperrt
 */
export const skills4RetailLane = (steps, konzept) => {
	if (!konzept) return null

	const endIndex = steps.findIndex(step => step.id === konzept.dueBeforeId)
	if (endIndex === -1) return null

	// Fehlt die Analyse (heute nie), beginnt die Spur am ersten Schritt.
	const analyse = steps.findIndex(step => step.id === 'riskAnalysis')
	const startIndex = analyse === -1 || analyse >= endIndex ? 0 : analyse

	const { dueBeforeId, phaseId, deadline, ...step } = konzept

	return { step, startIndex, endIndex, dueBeforeId, phaseId, deadline: deadline || null }
}

/**
 * Der Weg einer Junior Mini Company (Anforderung #92) — kürzer als bei den
 * anderen drei Formen: Die Kinder haben keine Konten, es gibt weder Berichte
 * noch Rechnungen. Alles erledigt die Lehrkraft.
 *
 * Die Schritte folgen den Abschnitten der JMC-Seite und dem, was der Server
 * vor dem Einreichen verlangt (`missingForSubmit` in
 * services/miniCompanies/rules.js im Backend): mindestens ein Kind und die
 * Einverständniserklärungen. Danach entscheidet die Landesadministration, und
 * am Ende stehen die Zertifikate.
 *
 * Nach einer Rückgabe ist „Prüfung" wieder offen — die Lehrkraft muss
 * nachbessern und erneut einreichen.
 *
 * @param miniCompany die JMC, wie sie GET …/mini-companies/:id liefert
 * @returns dieselbe Form wie buildCompanyProgress, ohne Skills4Retail-Spur
 */
export const buildMiniCompanyProgress = ({ miniCompany = {}, t }) => {
	const status = miniCompany.status
	const eingereicht = status === 'SUBMITTED' || status === 'ACTIVE'
	const aktiv = status === 'ACTIVE'

	const phases = [
		{
			id: 'registration',
			title: t('Registration'),
			steps: [
				{ id: 'jmcCreated', title: t('JMC step created'), icon: 'start', done: true },
				{
					id: 'jmcChildren',
					title: t('JMC step children'),
					icon: 'team',
					done: (miniCompany.participants || []).length > 0
				},
				{ id: 'jmcConsent', title: t('JMC step consent'), icon: 'document', done: !!miniCompany.consent }
			]
		},
		{
			id: 'approval',
			title: t('Approval'),
			steps: [
				{ id: 'review', title: t('Check'), icon: 'waiting', done: eingereicht },
				{ id: 'approved', title: t('Approved'), icon: 'approved', done: aktiv }
			]
		},
		{
			id: 'completion',
			title: t('JMC phase completion'),
			steps: [
				{
					id: 'certificate',
					title: t('JMC certificates'),
					icon: 'trophy',
					done: !!miniCompany.certificatesGeneratedAt
				}
			]
		}
	]

	const steps = phases.flatMap(phase => phase.steps)
	const current = steps.find(step => !step.done)
	const done = steps.filter(step => step.done).length

	return {
		phases,
		steps,
		lane: null,
		currentId: current?.id || null,
		done,
		total: steps.length,
		percent: steps.length ? Math.round((done / steps.length) * 100) : 0
	}
}
